import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { KPI_SECTIONS, KPI_STATUS, countKpiStatuses } from '../config/kpiRegistry';

// The dashboard's KPI area: Daily / Weekly / Monthly, then groups. Built KPIs are
// tiles with a real number; everything else is a small labelled chip, so the whole
// list is visible and every KPI states honestly whether it exists yet. Values come
// from the caller (`values`: { [key]: { value, sub, href? } }) — this component
// fetches nothing and invents nothing: a built KPI with no value shows a dash.
export default function KpiSections({ values = {} }) {
  const navigate = useNavigate();
  const totals = countKpiStatuses();

  return (
    <section className="kpi-sections" aria-label="Key performance indicators">
      <div className="kpi-sections__head">
        <h3 className="kpi-sections__title">Key performance indicators</h3>
        <div className="kpi-legend">
          {['live', 'elsewhere', 'next', 'data'].map((status) => (
            <span key={status} className="kpi-legend__item" title={KPI_STATUS[status].hint}>
              <span className={`kpi-chip kpi-chip--${status}`}>{KPI_STATUS[status].label}</span>
              <span className="kpi-legend__count">{totals[status]}</span>
            </span>
          ))}
        </div>
      </div>

      {KPI_SECTIONS.map((section) => (
        <div key={section.cadence} className="kpi-cadence">
          <h4 className="kpi-cadence__title">{section.cadence}</h4>

          {section.groups.map((group) => {
            const built = group.kpis.filter((k) => k.status === 'live');
            const rest = group.kpis.filter((k) => k.status !== 'live');
            return (
              <div key={group.title} className="kpi-group">
                <div className="kpi-group__title">{group.title}</div>

                {built.length > 0 && (
                  <div className="kpi-tiles">
                    {built.map((kpi) => {
                      const v = values[kpi.key];
                      const clickable = Boolean(v?.href);
                      return (
                        <div
                          key={`${group.title}-${kpi.label}`}
                          className={`kpi-tile${clickable ? ' kpi-tile--clickable' : ''}`}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          onClick={clickable ? () => navigate(v.href) : undefined}
                          onKeyDown={
                            clickable
                              ? (e) => { if (e.key === 'Enter' || e.key === ' ') navigate(v.href); }
                              : undefined
                          }
                        >
                          <div className="kpi-tile__label">{kpi.label}</div>
                          <div className="kpi-tile__value">{v ? v.value : '—'}</div>
                          <div className="kpi-tile__sub">{v ? v.sub : 'Could not load'}</div>
                          {clickable && (
                            <div className="kpi-tile__go">
                              View details <ChevronRight size={12} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {rest.length > 0 && (
                  <div className="kpi-pills">
                    {rest.map((kpi) =>
                      kpi.status === 'elsewhere' ? (
                        <button
                          key={`${group.title}-${kpi.label}`}
                          type="button"
                          className="kpi-pill kpi-pill--link"
                          title={KPI_STATUS.elsewhere.hint}
                          onClick={() => navigate(kpi.href)}
                        >
                          {kpi.label}
                          <span className={`kpi-chip kpi-chip--${kpi.status}`}>{KPI_STATUS.elsewhere.label}</span>
                        </button>
                      ) : (
                        <span
                          key={`${group.title}-${kpi.label}`}
                          className="kpi-pill"
                          title={KPI_STATUS[kpi.status].hint}
                        >
                          {kpi.label}
                          <span className={`kpi-chip kpi-chip--${kpi.status}`}>{KPI_STATUS[kpi.status].label}</span>
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <style>{`
        .kpi-sections { margin-bottom: var(--space-8); }
        .kpi-sections__head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-3); margin-bottom: var(--space-4); }
        .kpi-sections__title { font-size: var(--text-lg); font-weight: 600; }
        .kpi-legend { display: flex; gap: var(--space-4); flex-wrap: wrap; }
        .kpi-legend__item { display: inline-flex; align-items: center; gap: var(--space-2); }
        .kpi-legend__count { font-size: var(--text-xs); color: var(--text-tertiary); }

        .kpi-cadence { margin-bottom: var(--space-8); }
        .kpi-cadence__title { font-size: var(--text-base); font-weight: 600; margin-bottom: var(--space-3); padding-bottom: var(--space-2); border-bottom: 1px solid var(--divider); }
        .kpi-group { margin-bottom: var(--space-5); }
        .kpi-group__title { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-tertiary); margin-bottom: var(--space-2); }

        .kpi-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: var(--space-3); }
        .kpi-tile {
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          border-radius: var(--radius-lg);
          padding: var(--space-4);
          min-width: 0;
        }
        .kpi-tile--clickable { cursor: pointer; transition: all var(--duration-fast) var(--ease-out); }
        .kpi-tile--clickable:hover { border-color: var(--glass-border-hover); background: var(--glass-bg-hover); }
        .kpi-tile--clickable:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }
        .kpi-tile__label { font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-1); }
        .kpi-tile__value { font-size: var(--text-2xl); font-weight: 600; letter-spacing: -0.02em; }
        .kpi-tile__sub { font-size: var(--text-xs); color: var(--text-tertiary); margin-top: var(--space-1); }
        .kpi-tile__go { display: inline-flex; align-items: center; gap: 2px; font-size: var(--text-xs); color: var(--accent-primary); margin-top: var(--space-2); }

        .kpi-pills { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
        .kpi-pill {
          display: inline-flex; align-items: center; gap: var(--space-2);
          padding: 4px var(--space-3);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs); color: var(--text-secondary);
          background: transparent;
        }
        .kpi-pill--link { cursor: pointer; font-family: inherit; }
        .kpi-pill--link:hover { border-color: var(--accent-primary); color: var(--accent-primary); }

        .kpi-chip { display: inline-block; font-size: 10.5px; padding: 1px 7px; border-radius: var(--radius-sm); white-space: nowrap; }
        .kpi-chip--live { background: var(--status-success-subtle); color: var(--status-success); }
        .kpi-chip--elsewhere { background: var(--status-info-subtle); color: var(--status-info); }
        .kpi-chip--next { background: var(--accent-primary-subtle); color: var(--accent-primary); }
        .kpi-chip--data { background: transparent; color: var(--text-tertiary); border: 1px solid var(--border-default); }
      `}</style>
    </section>
  );
}
